import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { FiEdit } from "react-icons/fi";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { MdDelete } from "react-icons/md";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  FINANCIAL_YEAR,
  INSERT_ACCESS,
} from "../../_contstants/common";
import {
  deleteOfficeStaffOut,
  getAllOfficeStaffOut,
  updateStaffInTime,
} from "./services/officeStaffOut.services";
import {
  clearOfficeStaffOutID,
  setOfficeStaffOutID,
} from "./officeStaffOutSlice";
import moment from "moment";
import { TimePicker } from "@progress/kendo-react-dateinputs";

const OfficeStaffOut: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    return () => {
      dispatch(setCurrentPage(0));
    };
  }, []);

  const handleCreate = () => {
    navigate("/officestaffout/create");
  };

  return (
    <>
      <DeleteOfficeStaffOutDialog />
      <EditOfficeStaffInTimeDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Office Staff Out List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              {"Add Office Staff Out"}
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <OfficeStaffOutGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const OfficeStaffOutGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);

  const OfficeStaffOutList = useAppSelector(
    (state) => state.officeStaffOut.OfficeStaffOutList
  );
  const loading = useAppSelector((state) => state.officeStaffOut.loading);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);

  useEffect(() => {
    const payload = {
      user_name: "",
      financial_year: FINANCIAL_YEAR,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    dispatch(getAllOfficeStaffOut(payload));
  }, [currentPage, pageLimit]);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEdit = (staff_out_guid: any) => {
      navigate("/officestaffout/edit", {
        state: { staff_out_guid: staff_out_guid },
      });
    };
    const handleOpenDeleteDialog = (staff_out_guid: any) => {
      dispatch(openDialog("deleteOfficeStaffOut"));
      dispatch(setOfficeStaffOutID(staff_out_guid));
    };

    return (
      <td
        className={`${props?.className} action-td`}
        style={{
          ...props.style,
          display: "flex",
          justifyContent: "center",
        }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEdit(props.dataItem?.staff_out_guid)}
          >
            <FiEdit
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            onClick={() =>
              handleOpenDeleteDialog(props.dataItem?.staff_out_guid)
            }
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}
      </td>
    );
  };

  const handleOpenTimeEditDialog = (staff_out_guid: any) => {
    dispatch(openDialog("editTimeOfficeStaffOut"));
    dispatch(setOfficeStaffOutID(staff_out_guid));
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 9 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {OfficeStaffOutList && OfficeStaffOutList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          className="responsive-table"
          filterable={true}
          filter={filter}
          data={
            filter ? filterBy(OfficeStaffOutList, filter) : OfficeStaffOutList
          }
          onFilterChange={handleFilterChange}
          total={OfficeStaffOutList[0]?.itemcount || OfficeStaffOutList.length}
          skip={currentPage * pageLimit}
          take={pageLimit}
          pageable={{
            buttonCount: 5,
            pageSizes: [10, 50, 100, 300, 500],
            pageSizeValue: pageLimit,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="staff_out_no"
            title="No"
            filterable={false}
            width={100}
            cell={(props: any) => (
              <td>
                <div
                  className={isEditAccess ? "hoverableName" : "normalName"}
                  onClick={() =>
                    isEditAccess &&
                    navigate("/officestaffout/edit", {
                      state: {
                        staff_out_guid: props.dataItem?.staff_out_guid,
                      },
                    })
                  }
                >
                  {props.dataItem?.staff_out_no}
                </div>
              </td>
            )}
          />
          <Column field="staff_out_date" title="Out Date" />
          <Column field="out_time" title="Out Time" />
          <Column
            field="in_time"
            title="In Time"
            cell={(props: any) => (
              <td>
                {!props.dataItem?.in_time ? (
                  <div>
                    <a
                      style={{
                        color: "red",
                        textDecoration: "underline",
                        fontWeight: "bold",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        handleOpenTimeEditDialog(props.dataItem?.staff_out_guid)
                      }
                    >
                      Set in Time
                    </a>
                  </div>
                ) : (
                  <div>{props.dataItem?.in_time}</div>
                )}
              </td>
            )}
          />
          <Column field="user_name" title="Employee Name" />
          <Column field="reason_for_out" title="Reason" />
          <Column field="approvedby_name" title="Approved By" />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="id"
              title="Actions"
              cell={MyEditCommandCell}
              width={"110px"}
              locked={true}
              filterable={false}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const EditOfficeStaffInTimeDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const OfficeStaffOutID = useAppSelector(
    (state) => state.officeStaffOut.OfficeStaffOutID
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const [inTime, setInTime] = React.useState<string | Date>(
    moment().toDate() || ""
  );

  useEffect(() => {
    setInTime(moment().toDate());
  }, [OfficeStaffOutID]);

  const handleSubmit = async () => {
    if (OfficeStaffOutID) {
      const updatePayload = {
        staff_out_guid: OfficeStaffOutID,
        in_time: inTime ? moment(inTime).format("HH:mm:ss") : "",
      };
      const response = await dispatch(updateStaffInTime(updatePayload));
      dispatch(setCurrentPage(0));
      const payload = {
        user_name: "",
        financial_year: FINANCIAL_YEAR,
        limit: +pageLimit,
        skip: +currentPage * +pageLimit,
      };
      if (response.meta.requestStatus === "fulfilled") {
        dispatch(getAllOfficeStaffOut(payload));
        dispatch(closeDialog());
        dispatch(clearOfficeStaffOutID());
      } else {
        dispatch(getAllOfficeStaffOut(payload));
        dispatch(closeDialog());
        dispatch(clearOfficeStaffOutID());
      }
    }
  };

  return (
    <>
      {dialogName === "editTimeOfficeStaffOut" && (
        <AppDialog>
          <>
            <form className="k-form" onSubmit={handleSubmit}>
              <fieldset>
                <Typography.h5>{"Edit Office Staff In Time"}</Typography.h5>
                <GridLayout>
                  <GridLayoutItem>
                    <TimePicker
                      name="in_time"
                      label="In Time"
                      required={true}
                      format="HH:mm"
                      defaultValue={moment().toDate() || ""}
                      onChange={(e: any) => {
                        setInTime(e?.target?.value);
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                      marginTop: 20,
                    }}
                  >
                    <div>
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginRight: 4 }}
                        onClick={() => {
                          setInTime("");
                          dispatch(clearOfficeStaffOutID());
                          dispatch(closeDialog());
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        fillMode={"solid"}
                        themeColor={"primary"}
                        onClick={handleSubmit}
                      >
                        Update
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </fieldset>
            </form>
          </>
        </AppDialog>
      )}
    </>
  );
};

const DeleteOfficeStaffOutDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const OfficeStaffOutID = useAppSelector(
    (state) => state.officeStaffOut.OfficeStaffOutID
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);

  const handleDeleteAction = async (ID: any) => {
    if (ID) {
      const response = await dispatch(deleteOfficeStaffOut(ID));
      dispatch(setCurrentPage(0));
      const payload = {
        user_name: "",
        financial_year: FINANCIAL_YEAR,
        limit: +pageLimit,
        skip: +currentPage * +pageLimit,
      };
      if (response.payload.Data.Status === 200) {
        dispatch(getAllOfficeStaffOut(payload));
        dispatch(closeDialog());
        dispatch(clearOfficeStaffOutID());
      } else {
        dispatch(getAllOfficeStaffOut(payload));
        dispatch(closeDialog());
        dispatch(clearOfficeStaffOutID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteOfficeStaffOut" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Staff Out"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Staff Out?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(OfficeStaffOutID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default OfficeStaffOut;
